import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAudit, usePayouts, useUsers } from '../api/hooks.ts'
import { Button, Card, ErrorNote, Screen, SectionTitle } from '../components/ui.tsx'
import { eur, shortDate } from '../lib/format.ts'

const DAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']

function when(iso: string): string {
  const date = new Date(iso)
  const hora = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' }).format(date)
  const hoy = new Date()
  const mismoDia = date.toDateString() === hoy.toDateString()
  if (mismoDia) return `hoy ${hora}`
  return `${DAYS[date.getDay()]} ${date.getDate()} · ${hora}`
}

/**
 * Registro de acciones.
 *
 * Existe para resolver un «yo no fui» o un «¿quién me quitó los 4,08 €?». Por eso
 * se lee como frases y no como filas de una tabla técnica: quien lo consulta
 * está zanjando una discusión, no depurando.
 */
export function AuditScreen() {
  const [date, setDate] = useState('')
  const [subject, setSubject] = useState('')
  const audit = useAudit({ date: date || undefined, subject: subject || undefined })
  const users = useUsers()

  const children = (users.data ?? []).filter((u) => u.role === 'CHILD')
  const entries = audit.data ?? []
  const filtrando = date !== '' || subject !== ''

  return (
    <Screen>
      <h1 className="mb-1 text-xl font-bold tracking-tight">Registro</h1>
      <p className="mb-4 text-sm text-[var(--color-muted)]">
        Lo pagado cada semana, y quién hizo qué.
      </p>

      <Payouts />

      <SectionTitle>Acciones</SectionTitle>

      {/* Los filtros van antes de la lista: se viene aquí a buscar algo
          concreto —«¿qué pasó el martes?»— más que a leerlo entero. */}
      <div className="mb-3 flex flex-col gap-2">
        <div className="flex gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="min-h-11 flex-1 rounded-lg border border-[var(--color-line)] bg-[var(--color-canvas)] px-3 text-[16px]"
          />
          {filtrando && (
            <Button
              variant="secondary"
              onClick={() => {
                setDate('')
                setSubject('')
              }}
            >
              Quitar filtros
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            { value: '', label: 'Todo' },
            ...children.map((c) => ({ value: c.id, label: c.name })),
            { value: 'general', label: 'Generales' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setSubject(option.value)}
              className={`min-h-11 rounded-xl border px-3 text-sm ${
                subject === option.value
                  ? 'border-[var(--color-brand)] bg-[var(--color-brand)]/10 text-[var(--color-brand)]'
                  : 'border-[var(--color-line)]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {audit.error != null && <ErrorNote error={audit.error} />}

      <Card>
        {audit.isLoading && <p className="py-2 text-sm text-[var(--color-muted)]">Buscando…</p>}
        {!audit.isLoading && entries.length === 0 && (
          <p className="py-2 text-sm text-[var(--color-muted)]">
            {filtrando
              ? 'No hay acciones que encajen con esta búsqueda.'
              : 'Todavía no hay nada registrado.'}
          </p>
        )}
        {entries.map((entry) => (
          <div key={entry.id} className="border-b border-[var(--color-line)] py-3 last:border-b-0">
            <p className="text-[15px]">
              <strong>{entry.actorName}</strong> {entry.actionLabel}{' '}
              {/* Muchas etiquetas terminan en preposición («un aviso por»), así
                  que sin sujeto la frase quedaría a medias. */}
              {entry.subject ? (
                <>«{entry.subject}»</>
              ) : (
                <span className="text-[var(--color-muted)]">una tarea que ya no existe</span>
              )}
            </p>
            <p className="text-xs text-[var(--color-muted)]">
              {when(entry.createdAt)}
              {/* De quién es la acción sólo hace falta decirlo cuando se están
                  viendo mezcladas. */}
              {subject === '' && entry.childName && ` · ${entry.childName}`}
            </p>
          </div>
        ))}
      </Card>
    </Screen>
  )
}

/**
 * Lo que ha cobrado cada uno, semana a semana.
 *
 * El día de pago la pregunta no es «¿cómo va la semana?» sino «¿cuánto le doy a
 * cada uno?». Esta tabla responde eso de un vistazo y, pasado el tiempo, sirve
 * de recibo: queda escrito lo que se pagó y cuándo.
 */
function Payouts() {
  const payouts = usePayouts()
  const weeks = payouts.data ?? []

  if (payouts.error) return <ErrorNote error={payouts.error} />
  if (weeks.length === 0) {
    return (
      <>
        <SectionTitle>Pagos por semana</SectionTitle>
        <Card>
          <p className="py-2 text-sm text-[var(--color-muted)]">
            Todavía no se ha cerrado ninguna semana. Aparecerán aquí a partir del primer viernes.
          </p>
        </Card>
      </>
    )
  }

  return (
    <>
      <SectionTitle>Pagos por semana</SectionTitle>
      <Card>
        {weeks.map((week) => (
          <div
            key={week.weekStart}
            className="border-b border-[var(--color-line)] py-3 last:border-b-0"
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-[15px]">
                {shortDate(week.weekStart)} – {shortDate(week.weekEnd)}
              </p>
              <span className="shrink-0 font-semibold tabular-nums">{eur(week.totalCents)}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
              {week.rows.map((row) => (
                <Link
                  key={row.weekId}
                  to={`/semana/${row.weekId}/desglose`}
                  className="text-xs text-[var(--color-muted)]"
                >
                  {row.name}{' '}
                  <span className="tabular-nums text-[var(--color-ink)]">
                    {eur(row.payoutCents)}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </Card>
    </>
  )
}
